import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Clock, ArrowRight, RefreshCw, Sparkles } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const c = {
  primary: '#B91C1C', text: '#0A0A0A', textSecondary: '#525252', textTertiary: '#A3A3A3',
  background: '#FAFAF9', surface: '#FFFFFF', border: '#E5E5E5', success: '#059669', gold: '#B45309',
};

const FellowshipPaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying');
  const [enrollment, setEnrollment] = useState(null);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const orderId = searchParams.get('order_id');

  const verifyPayment = useCallback(async () => {
    setStatus('verifying');
    try {
      const res = await axios.get(`${API}/fellowship/payment-status/${orderId}`);
      setEnrollment(res.data);
      if (['confirmed', 'completed'].includes(res.data.status)) setStatus('success');
      else if (res.data.status === 'payment_failed') setStatus('failed');
      else if (res.data.status === 'pending_payment') {
        setStatus('pending');
        if (retryCount < 3) setTimeout(() => { setRetryCount((p) => p + 1); verifyPayment(); }, 3000);
      }
    } catch (err) {
      setStatus('failed');
      setError(err.response?.data?.detail || 'Failed to verify payment');
    }
  }, [orderId, retryCount]);

  useEffect(() => {
    if (orderId) verifyPayment();
    else { setStatus('failed'); setError('Invalid payment callback - missing order ID'); }
  }, [orderId]);

  const fmt = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: c.background, color: c.text }}>
      <div className="w-full max-w-md">
        <Link to="/fellowship" className="flex items-center justify-center gap-2 mb-6">
          <div className="w-6 h-6 flex items-center justify-center" style={{ background: c.primary, borderRadius: '2px' }}><Sparkles size={11} className="text-white" /></div>
          <span className="font-extrabold text-sm tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>EvalentX Fellowship</span>
        </Link>

        {status === 'verifying' && (
          <div className="bg-white border p-8 text-center" style={{ borderColor: c.border, borderRadius: '2px' }} data-testid="payment-verifying">
            <Loader2 className="animate-spin mx-auto" size={36} style={{ color: c.primary }} />
            <h1 className="text-xl font-extrabold mt-4 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Verifying Payment</h1>
            <p className="text-sm mt-1" style={{ color: c.textSecondary }}>Confirming your enrollment{retryCount > 0 ? ` (attempt ${retryCount + 1})` : ''}…</p>
          </div>
        )}

        {status === 'success' && enrollment && (
          <div className="bg-white border p-8 text-center" style={{ borderColor: c.success, borderRadius: '2px' }} data-testid="payment-success">
            <div className="w-16 h-16 mx-auto flex items-center justify-center" style={{ background: '#ECFDF5', borderRadius: '2px' }}><CheckCircle size={32} style={{ color: c.success }} /></div>
            <h1 className="text-2xl font-extrabold mt-4 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Payment Successful!</h1>
            <p className="text-sm mt-1" style={{ color: c.textSecondary }}>You're enrolled in the EvalentX Fellowship.</p>
            <div className="mt-5 border text-left p-4 space-y-2" style={{ borderColor: c.border, background: c.background, borderRadius: '2px' }}>
              <Row label="Program" value={enrollment.fellowship_title} />
              <Row label="Amount Paid" value={fmt(enrollment.amount)} accent />
              <Row label="Order ID" value={orderId} mono />
            </div>
            <Link to="/fellowship" className="mt-6 flex items-center justify-center gap-2 w-full py-3 text-sm font-bold tracking-[0.1em] uppercase text-white transition-transform hover:-translate-y-0.5" style={{ background: c.primary, borderRadius: '2px' }} data-testid="back-to-fellowship">
              Back to Fellowship <ArrowRight size={16} />
            </Link>
          </div>
        )}

        {status === 'pending' && (
          <div className="bg-white border p-8 text-center" style={{ borderColor: c.gold, borderRadius: '2px' }} data-testid="payment-pending">
            <div className="w-16 h-16 mx-auto flex items-center justify-center" style={{ background: '#FFFBEB', borderRadius: '2px' }}><Clock size={32} style={{ color: c.gold }} /></div>
            <h1 className="text-xl font-extrabold mt-4 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Payment Pending</h1>
            <p className="text-sm mt-1" style={{ color: c.textSecondary }}>Your payment is being processed.</p>
            <button onClick={() => { setRetryCount(0); verifyPayment(); }} className="mt-6 flex items-center justify-center gap-2 w-full py-3 text-sm font-bold tracking-[0.1em] uppercase border" style={{ borderColor: c.text, borderRadius: '2px' }} data-testid="retry-verification-btn">
              <RefreshCw size={16} /> Check Again
            </button>
            <p className="text-[11px] mt-3" style={{ color: c.textTertiary }}>If debited, it will be verified automatically.</p>
          </div>
        )}

        {status === 'failed' && (
          <div className="bg-white border p-8 text-center" style={{ borderColor: c.primary, borderRadius: '2px' }} data-testid="payment-failed">
            <div className="w-16 h-16 mx-auto flex items-center justify-center" style={{ background: '#FEF2F2', borderRadius: '2px' }}><XCircle size={32} style={{ color: c.primary }} /></div>
            <h1 className="text-xl font-extrabold mt-4 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Payment Failed</h1>
            <p className="text-sm mt-1" style={{ color: c.textSecondary }}>{error || "We couldn't verify your payment."}</p>
            <p className="text-[11px] mt-3" style={{ color: c.textTertiary }}>If money was deducted, it will be refunded within 5–7 business days.</p>
            <button onClick={() => navigate('/fellowship')} className="mt-6 flex items-center justify-center gap-2 w-full py-3 text-sm font-bold tracking-[0.1em] uppercase text-white" style={{ background: c.primary, borderRadius: '2px' }} data-testid="try-again-btn">
              Back to Fellowship
            </button>
          </div>
        )}

        <p className="text-center text-sm mt-6" style={{ color: c.textTertiary }}>
          Need help? <a href="mailto:support@evalentx.com" className="font-semibold hover:underline" style={{ color: c.primary }}>support@evalentx.com</a>
        </p>
      </div>
    </div>
  );
};

const Row = ({ label, value, accent, mono }) => (
  <div className="flex justify-between gap-3 text-sm">
    <span style={{ color: c.textTertiary }}>{label}</span>
    <span className="font-semibold text-right truncate max-w-[220px]" style={{ color: accent ? c.success : c.text, fontFamily: mono ? "'IBM Plex Mono', monospace" : 'inherit' }}>{value}</span>
  </div>
);

export default FellowshipPaymentCallback;
